import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import { withRouter } from 'react-router-dom';
import { Button, Drawer, Dropdown, Menu } from 'antd';
import Configuration from "../../components/Configuration/Configuration";
import EditConfiguration from "../../components/EditConfiguration/EditConfiguration";
import NewConfiguration from "../../components/NewConfiguration/NewConfiguration";
import classes from './Configurations.module.scss';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTools } from '@fortawesome/free-solid-svg-icons';
import DataTable from '../../components/DataTable/DataTable';
import StringFormatter from '../../shared/stringFormatter';
import { isStringNotEmpty } from '../../shared/objectUtils';
import { DownOutlined, EditOutlined } from '@ant-design/icons';

const stringFormatter = new StringFormatter();

const Configurations = (props) => {
    //#region constants

    const fullWidth = global.window.innerWidth;
    const singularEntityName = "Configuration";
    const pluralEntityName = "Configurations";

    //#endregion
    //#region useDispatch and useSelectors

    const dispatch = useDispatch();
    const isLoading = useSelector(state => state.configurations.isLoading);
    const records = useSelector(state => state.configurations.records);
    const pagination = useSelector(state => state.configurations.pagination);

    //#endregion
    //#region useStates

    const [selectedRecord, setSelectedRecord] = useState({});
    const [showEditEntity, setShowEditEntity] = useState(false);
    const [showNewEntity, setShowNewEntity] = useState(false);

    //#endregion
    //#region toggles

    const toggleEditEntity = () => {
        setShowEditEntity(!showEditEntity);
    };

    const toggleNewEntity = () => {
        setShowNewEntity(!showNewEntity);
    };

    //#endregion
    //#region table methods

    const refreshTable = () => {
        let searchParams = {
            page: 1,
            size: 5,
            isDeleted: false
        };

        dispatch(actionCreators.fetchConfigurations(searchParams));
    };

    const handleTableChange = (pagination, filters, sorter) => {
        const pager = { ...pagination };
        pager.current = pagination.current;
        dispatch(actionCreators.fetchConfigurationsSuccess({ pagination: pager }));

        let searchParams = {
            page: pagination.current,
            size: pagination.pageSize,
            isDeleted: false
        };

        if (isStringNotEmpty(sorter.order)) {
            const sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
            searchParams.order = sortOrder;
        }
        if (isStringNotEmpty(sorter.field)) {
            searchParams.sort = sorter.field;
        }

        dispatch(actionCreators.fetchConfigurations(searchParams));
    };

    //#endregion
    //#region table displays

    const menu = (record) => {
        return (
            <Menu>
                <Menu.Item key="editEntity" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); toggleEditEntity(); }}>
                    <EditOutlined style={{ marginRight: 8 }} />
                    <span>Edit {singularEntityName} Details</span>
                </Menu.Item>
            </Menu>
        );
    };

    const columns = [
        {
            title: <FontAwesomeIcon icon={faTools} size="lg" />,
            key: 'actions',
            width: 50,
            render: (text, record) => {
                return (
                    <Dropdown overlay={menu(record)} trigger={['click']}>
                        <Button>Actions <DownOutlined /></Button>
                    </Dropdown>
                );
            },
            align: 'center',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'GeoFence Radius',
            dataIndex: 'geoFenceRadius',
            key: 'geoFenceRadius',
            render: (text, record) => { return stringFormatter.toFormattedString("Distance", text, record.geoFenceRadiusUnit); },
        },
        {
            title: 'Location Distance Interval',
            dataIndex: 'locationDistanceInterval',
            key: 'locationDistanceInterval',
            render: (text, record) => { return stringFormatter.toFormattedString("Distance", text, record.locationDistanceIntervalUnit); },
        },
        {
            title: 'Location Time Interval',
            dataIndex: 'locationTimeInterval',
            key: 'locationTimeInterval',
            render: (text, record) => { return stringFormatter.toFormattedString("Time", text, record.locationTimeIntervalUnit); },
        },
        {
            title: 'Customer Support Phone #',
            dataIndex: 'companyPhoneNumber',
            key: 'companyPhoneNumber',
            render: (text, record) => { return stringFormatter.toFormattedPhoneNumber(text); },
        }
    ];

    //#endregion
    //#region displays

    const newEntityComponents = (
        <Drawer
            title={"New " + singularEntityName}
            onClose={toggleNewEntity}
            visible={showNewEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <NewConfiguration
                cancel={toggleNewEntity}
            />
        </Drawer>
    );

    const editEntityComponents = (
        <Drawer
            title={"Edit " + singularEntityName + " Details"}
            onClose={toggleEditEntity}
            visible={showEditEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <EditConfiguration
                cancel={toggleEditEntity}
                configuration={selectedRecord}
            />
        </Drawer>
    );

    //#endregion
    //#region useEffects

    useEffect(() => {
        refreshTable();
    }, []);

    //#endregion

    return (
        <DataTable
            dataSource={records}
            columns={columns}
            pagination={pagination}
            onChange={handleTableChange}
            loading={isLoading === true}
            expandedRowRender={record => (<Configuration configuration={record} />)}
            rowClassName={classes.dataTableRow}
            singularEntityName={singularEntityName}
            pluralEntityName={pluralEntityName}
            newEntityAction={toggleNewEntity}
            rowKey={record => record.id}
            style={{ backgroundColor: '#ffffff' }}
        >
            {newEntityComponents}
            {editEntityComponents}
        </DataTable>
    );
};

export default withRouter(Configurations);